import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, CalendarDays, BarChart3, Upload, ShieldCheck, UserCog, LogOut, Bell, AlertTriangle, Menu, FileBarChart, Settings as SettingsIcon, Download, WifiOff, Copy, Stethoscope } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCrm } from "@/context/CrmContext";
import { GlobalSearch } from "@/components/GlobalSearch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { api } from "@/lib/api";
import { ROLE_LABELS, ago } from "@/lib/constants";
import { useInstall, useOnline } from "@/lib/pwa";

const CRM = ["ADMIN", "SALES_MANAGER", "SALES_EXEC"];
const NAV = [
  { to: "/", t: "Today", icon: LayoutDashboard, end: true, roles: CRM },
  { to: "/leads", t: "Leads", icon: Users, roles: CRM },
  { to: "/meetings", t: "Meetings", icon: CalendarDays, roles: CRM },
  { to: "/analytics", t: "Insights", icon: BarChart3, roles: CRM },
  { to: "/clinic", t: "Rahmat Clinic", icon: Stethoscope, roles: ["ADMIN", "SALES_MANAGER", "CLINIC_STAFF"] },
  { to: "/reports", t: "Weekly Report", icon: FileBarChart, roles: ["ADMIN", "SALES_MANAGER"] },
  { to: "/import", t: "Import", icon: Upload, roles: ["ADMIN", "SALES_MANAGER"] },
  { to: "/duplicates", t: "Duplicates", icon: Copy, roles: ["ADMIN", "SALES_MANAGER"] },
  { to: "/team", t: "Team", icon: UserCog, roles: ["ADMIN", "SALES_MANAGER"] },
  { to: "/audit", t: "Audit Log", icon: ShieldCheck, roles: ["ADMIN"] },
  { to: "/settings", t: "Settings", icon: SettingsIcon },
];

const NavItems = ({ items, onClick }) => items.map(({ to, t, icon: Icon, end }) => (
  <NavLink key={to} to={to} end={end} onClick={onClick} data-testid={`nav-item-${t.toLowerCase().replace(/\s/g, "-")}`}
    className={({ isActive }) => `flex items-center gap-3 rounded-full px-4 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-[#232323] text-white" : "text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"}`}>
    <Icon className="h-4 w-4" />{t}
  </NavLink>
));

const InstallButton = () => {
  const { canInstall, installed, promptInstall } = useInstall();
  if (installed || !canInstall) return null;
  return (
    <button data-testid="install-app-btn" onClick={promptInstall} className="hidden h-10 items-center gap-1.5 rounded-full bg-[#F5E04F] px-4 text-sm font-semibold text-[#151515] sm:flex">
      <Download className="h-4 w-4" />Install app
    </button>
  );
};

const OfflineChip = () => {
  const online = useOnline();
  if (online) return null;
  return <span data-testid="offline-indicator" className="flex h-10 items-center gap-1.5 rounded-full bg-[#232323] px-3 text-xs font-semibold text-[#F5E04F]"><WifiOff className="h-4 w-4" />Offline</span>;
};

const Notifications = () => {
  const [data, setData] = useState({ items: [], unread: 0 });
  const { tick } = useCrm();
  const nav = useNavigate();
  useEffect(() => {
    const load = () => api.get("/notifications").then(({ data: response }) => {
      // The PHP API returns the notification rows directly; older API variants
      // may wrap them in { items, unread }.
      const items = Array.isArray(response) ? response : Array.isArray(response?.items) ? response.items : [];
      const unread = Number.isFinite(response?.unread) ? response.unread : items.filter((item) => !item.read_at).length;
      setData({ items, unread });
    }).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [tick]);
  return (
    <Popover onOpenChange={(o) => o && data.unread && api.post("/notifications/read").then(() => setData((d) => ({ ...d, unread: 0 })))}>
      <PopoverTrigger data-testid="notifications-btn" className="relative flex h-10 w-10 items-center justify-center rounded-lg border border-neutral-200 bg-white text-neutral-600">
        <Bell className="h-4 w-4" />
        {data.unread > 0 && <span data-testid="notifications-unread" className="absolute -right-1 -top-1 rounded-full bg-rose-500 px-1.5 font-mono text-[10px] text-white">{data.unread}</span>}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 border-neutral-200 bg-white p-0 text-neutral-800">
        <p className="border-b border-neutral-200 px-4 py-3 text-sm font-semibold">Alerts</p>
        <div className="max-h-80 overflow-y-auto">
          {data.items.length === 0 && <p className="p-4 text-sm text-neutral-400">You're all caught up.</p>}
          {data.items.map((n) => (
            <button key={n.id} onClick={() => n.lead_id && nav(`/leads/${n.lead_id}`)} className="block w-full border-b border-neutral-200/60 px-4 py-3 text-left hover:bg-neutral-100/40">
              <p className="text-sm font-medium">{n.title}</p><p className="text-xs text-neutral-500">{n.message || n.body}</p><p className="mt-1 font-mono text-[10px] text-neutral-400">{ago(n.created_at)}</p>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const PendingBanner = () => {
  const [items, setItems] = useState([]);
  const { tick, openDisposition } = useCrm();
  useEffect(() => { api.get("/meetings", { params: { state: "PENDING_DISPOSITION", mine: true } }).then((r) => setItems(r.data)).catch(() => {}); }, [tick]);
  if (!items.length) return null;
  const m = items[0];
  return (
    <div data-testid="pending-disposition-banner" className="flex items-center gap-3 border-b border-rose-300 bg-rose-50 px-4 py-2.5 text-sm text-rose-700">
      <AlertTriangle className="h-4 w-4 shrink-0 text-rose-700" />
      <span className="min-w-0 flex-1 truncate"><b>{items.length}</b> meeting{items.length > 1 ? "s" : ""} awaiting outcome — pipeline actions locked for {m.lead.full_name}</span>
      <button data-testid="pending-banner-log-btn" onClick={() => openDisposition(m.lead, m)} className="shrink-0 rounded-md bg-rose-500 px-3 py-1.5 text-xs font-semibold text-white">Log now</button>
    </div>
  );
};

export default function Layout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const items = NAV.filter((n) => !n.roles || n.roles.includes(user.role));
  const isClinic = user.role === "CLINIC_STAFF";
  const bottom = items.slice(0, 4);
  const UserBox = () => (
    <div className="border-t border-neutral-200 p-4">
      <p data-testid="current-user-name" className="truncate text-sm font-semibold text-neutral-800">{user.name}</p>
      <p data-testid="current-user-role" className="font-mono text-[11px] uppercase tracking-wider text-neutral-400">{ROLE_LABELS[user.role]}</p>
      <button data-testid="logout-btn" onClick={logout} className="mt-3 flex items-center gap-2 text-sm text-neutral-500 hover:text-rose-700"><LogOut className="h-4 w-4" />Sign out</button>
    </div>
  );
  const Brand = () => (
    <div className="flex items-center gap-2 px-4 py-5">
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1F9E7A] font-display text-sm font-bold text-white">{isClinic ? "RC" : "SI"}</div>
      <div><p className="font-display text-sm font-bold text-[#1A1A1A]">{isClinic ? "Rahmat Clinic" : "Saminfratech"}</p><p className="font-mono text-[10px] uppercase tracking-widest text-neutral-400">{isClinic ? "Patient Register" : "Sales Engine"}</p></div>
    </div>
  );
  return (
    <div className="min-h-screen bg-[#F2F4F1] text-neutral-800">
      <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col border-r border-neutral-200 bg-white lg:flex">
        <Brand /><nav className="flex-1 space-y-1 px-3"><NavItems items={items} /></nav><UserBox />
      </aside>
      <div className="lg:pl-64">
        <header className="sticky top-0 z-40 border-b border-neutral-200 bg-[#F2F4F1]/85 backdrop-blur-xl">
          <div className="flex h-16 items-center gap-3 px-4 sm:px-6">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger data-testid="mobile-menu-btn" className="flex h-10 w-10 items-center justify-center rounded-lg border border-neutral-200 lg:hidden"><Menu className="h-4 w-4" /></SheetTrigger>
              <SheetContent side="left" className="flex w-64 flex-col border-neutral-200 bg-white p-0 text-neutral-800">
                <Brand /><nav className="flex-1 space-y-1 px-3"><NavItems items={items} onClick={() => setOpen(false)} /></nav><UserBox />
              </SheetContent>
            </Sheet>
            <div className="flex-1">{!isClinic && <GlobalSearch />}</div>
            <OfflineChip />
            <InstallButton />
            {!isClinic && <Notifications />}
          </div>
          {!isClinic && <PendingBanner />}
        </header>
        <main className="mx-auto max-w-6xl px-4 pb-28 pt-6 sm:px-6 lg:pb-10"><Outlet /></main>
      </div>
      <nav className="fixed inset-x-0 bottom-0 z-40 grid h-16 border-t border-neutral-200 bg-white/95 backdrop-blur-xl lg:hidden" style={{ gridTemplateColumns: `repeat(${bottom.length}, minmax(0, 1fr))` }}>
        {bottom.map(({ to, t, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end} data-testid={`bottom-nav-${t.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) => `flex flex-col items-center justify-center gap-1 text-[11px] font-medium ${isActive ? "text-[#1F9E7A]" : "text-neutral-400"}`}>
            <Icon className="h-5 w-5" />{t}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
